import { Button, Divider, Flex, Grid, ScrollArea, Stack, Switch, Title } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useEffect, useRef } from 'react';

import { Customer } from '@e-origin/shared';

import { LeavePageNotification, StickyContainer, TextInput } from '@components';
import { EditCustomerType } from '@enums';
import { useDeclarant } from '@hooks/declarant';
import { useCustomerViewDeclarantFormInitializer } from './useCustomerViewDeclarantFormInitializer';

interface CustomerViewDeclarantProps {
  handleSubmitCustomer(type: EditCustomerType, childForm: UseFormReturnType<any>): void;
  customerDetails: Customer;
  isCreate: boolean;
  isButtonDisabled: boolean;
  isBE: boolean;
}

export const CustomerViewDeclarant: React.FC<CustomerViewDeclarantProps> = ({ handleSubmitCustomer, customerDetails, isButtonDisabled, isCreate, isBE }) => {
  const { data: generalDeclarant } = useDeclarant();

  const { form, buildFormValues } = useCustomerViewDeclarantFormInitializer({ generalDeclarant, customerDetails });
  const { declarant } = form.values;

  const storedDeclarantValues = useRef<typeof declarant | null>(null);

  const disableNameAndAddress = !isBE && declarant.useImporterEORI;

  useEffect(() => {
    form.setValues(buildFormValues(generalDeclarant, customerDetails));
    form.resetDirty({} as any);
  }, [generalDeclarant, customerDetails]);

  return (
    <>
      <LeavePageNotification hasUnsavedChanges={form.isDirty()} />

      <ScrollArea h="100%" offsetScrollbars="y">
        <Stack mx="xl">
          <Stack mb="xl">
            <Grid>
              <Grid.Col span={4}>
                <TextInput {...form.getInputProps('declarant.name')} label="Name" disabled={disableNameAndAddress} />
              </Grid.Col>
              {isBE && (
                <Grid.Col span={3}>
                  <TextInput {...form.getInputProps('declarant.identificationNumber')} label="Identification number" />
                </Grid.Col>
              )}
            </Grid>
            <Grid>
              <Grid.Col span={4}>
                <TextInput
                  {...form.getInputProps('declarant.identificationNumberIDMS')}
                  label={`Identification number ${isBE ? 'IDMS' : 'DMS'}`}
                  disabled={declarant.useImporterEORI}
                />
              </Grid.Col>
              <Grid.Col span={4}>
                <Flex align="center" h="100%">
                  <Switch
                    label="Use Importer/Exporter EORI"
                    labelPosition="left"
                    size="sm"
                    checked={declarant.useImporterEORI}
                    onChange={(event) => {
                      if (event.currentTarget.checked) {
                        storedDeclarantValues.current = { ...declarant };

                        form.setValues((current) => ({
                          ...current,
                          declarant: {
                            ...current.declarant,
                            name: '',
                            identificationNumberIDMS: '',
                            address: {
                              street: '',
                              postcode: '',
                              city: '',
                              country: '',
                            },
                          },
                        }));
                      } else {
                        if (storedDeclarantValues.current) {
                          form.setValues((current) => ({
                            ...current,
                            declarant: {
                              ...current.declarant,
                              ...storedDeclarantValues.current,
                            },
                          }));
                        }
                      }
                      form.setFieldValue('declarant.useImporterEORI', event.currentTarget.checked);
                    }}
                  />
                </Flex>
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={4}>
                <TextInput {...form.getInputProps('declarant.address.street')} label="Address" disabled={disableNameAndAddress} />
              </Grid.Col>
              <Grid.Col span={2}>
                <TextInput {...form.getInputProps('declarant.address.postcode')} label="Postcode" disabled={disableNameAndAddress} />
              </Grid.Col>
              <Grid.Col span={2}>
                <TextInput {...form.getInputProps('declarant.address.city')} label="City" disabled={disableNameAndAddress} />
              </Grid.Col>
              <Grid.Col span={2}>
                <TextInput {...form.getInputProps('declarant.address.country')} label="Country" disabled={disableNameAndAddress} />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={4}>
                <TextInput {...form.getInputProps('declarant.authorisedIdentity')} label="Authorised identity" />
              </Grid.Col>
            </Grid>
          </Stack>

          <Divider mt="xl" />

          <Stack my="xl">
            <Title order={5} fw={600}>
              Contact person
            </Title>
            <Grid>
              <Grid.Col span={2.5}>
                <TextInput {...form.getInputProps('declarant.contactPerson.name')} label="Name" />
              </Grid.Col>
              <Grid.Col span={2.5}>
                <TextInput {...form.getInputProps('declarant.contactPerson.phoneNumber')} label="Phone number" />
              </Grid.Col>
              <Grid.Col span={2.5}>
                <TextInput {...form.getInputProps('declarant.contactPerson.email')} label="E-mail" />
              </Grid.Col>
            </Grid>
          </Stack>
        </Stack>

        <StickyContainer>
          <Button
            disabled={isButtonDisabled || !form.isValid()}
            onClick={() => handleSubmitCustomer(EditCustomerType.DECLARANT, form)}
            type="submit"
            variant="filled"
            size="lg"
          >
            {isCreate ? 'Create' : 'Save'}
          </Button>
        </StickyContainer>
      </ScrollArea>
    </>
  );
};
